import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { leadListApi, type LeadListResponse, type LeadListStatus, type LeadListItem } from '../services/leadListApi';
import { PushedIndicator } from '../components/PushedIndicator';
import { SkipMarker } from '../components/SkipMarker';
import { PushConfirmModal } from '../components/PushConfirmModal';
import { CopyButton } from '../components/CopyButton';
import { AddPhoneInline } from '../components/AddPhoneInline';
import { SkipTraceInputs, SkipTraceReadyDot } from '../components/SkipTraceInputs';
import { LeadListDocCopy } from '../components/LeadListDocCopy';
import { SkipTraceOutcomeIndicators } from '../components/SkipTraceOutcomeIndicators';
import { SkipTraceCandidatesModal } from '../components/SkipTraceCandidatesModal';
import { formatNamesForTruePeopleSearch } from '../components/useRosterSelection';
import { HasContactIndicator } from '../components/HasContactIndicator';
import { SkipTraceCsvUploadModal } from '../components/SkipTraceCsvUploadModal';
import { useSkipTrace } from '../components/useSkipTrace';
import { SkipTracePanels } from '../components/SkipTracePanels';

// /mi/leads — staging area between selection and Close push.
//
// Items are added via the SelectionBar's "+ Lead list" button on AOI
// detail and Pool order detail. Each row preserves the (party_id,
// source_kind, source_id) tuple that put it on the list; bulk push
// dedupes parties at push time and renders per-party briefs with each
// party's original source context.
//
// Filter pills at top: Pending · Researched · Pushed · Dropped.
// Default selection: pending + researched (the working set). Pushed
// and Dropped are accessible by toggling.

const STATUS_LABELS: Record<LeadListStatus, string> = {
  pending:    'Pending',
  researched: 'Researched',
  pushed:     'Pushed',
  dropped:    'Dropped',
};

const STATUS_PILL_CLASS: Record<LeadListStatus, string> = {
  pending:    'border-accent/40 bg-accent-soft text-accent-bright',
  researched: 'border-blue/40 bg-blue/10 text-blue',
  pushed:     'border-green/40 bg-green/10 text-green',
  dropped:    'border-text-faint/40 bg-text-faint/10 text-text-faint',
};

const entityChipClass = (type: string | null | undefined): string => {
  if (!type) return 'bg-text-faint/15 text-text-faint';
  if (['llc', 'corp', 'partnership'].includes(type)) return 'bg-amber/15 text-amber';
  if (type === 'trust') return 'bg-blue/15 text-blue';
  if (type === 'individual') return 'bg-green/15 text-green';
  return 'bg-text-faint/15 text-text-faint';
};

// Filter state lives in the URL. Browser back/forward + bookmarking +
// shareable links all get free persistence. Default filter set is
// pending + researched; query-param "status" overrides.
const DEFAULT_STATUSES: LeadListStatus[] = ['pending', 'researched'];
const ALL_STATUSES: LeadListStatus[] = ['pending', 'researched', 'pushed', 'dropped'];

function parseStatusesFromUrl(sp: URLSearchParams): LeadListStatus[] {
  const raw = sp.get('status');
  if (raw === null) return DEFAULT_STATUSES;
  const parts = raw.split(',').map(s => s.trim()).filter(s => ALL_STATUSES.includes(s as LeadListStatus)) as LeadListStatus[];
  return parts.length > 0 ? parts : DEFAULT_STATUSES;
}

// Scroll + view-state storage keys are JURISDICTION-suffixed so the
// Weld lead list and the Wyoming lead list remember their own state
// independently. The CO suffix preserves the old key shape — that
// branch is loadable in either form during the first reload after
// this deploy lands (see loadStoredFilters fallback below).
const SCROLL_STORAGE_KEY_BY    = (j: 'CO' | 'WY') => `mi:leadlist:scroll:${j}`;
const FILTERS_STORAGE_KEY_BY   = (j: 'CO' | 'WY') => `mi:leadlist:filters:${j}`;
const COLLAPSED_STORAGE_KEY_BY = (j: 'CO' | 'WY') => `mi:leadlist:collapsed:${j}`;
// Legacy keys (pre-jurisdiction). Read on first hydrate for CO so
// the operator's last view survives the schema change.
const LEGACY_FILTERS_KEY   = 'mi:leadlist:filters';
const LEGACY_COLLAPSED_KEY = 'mi:leadlist:collapsed';

interface StoredFilters {
  statuses: LeadListStatus[];
  hide_skipped: boolean;
  needs_location: boolean;
  skip_ready: boolean;
  skip_traced: boolean;
}

function loadStoredFilters(jurisdiction: 'CO' | 'WY'): StoredFilters | null {
  if (typeof window === 'undefined') return null;
  try {
    let raw = window.localStorage.getItem(FILTERS_STORAGE_KEY_BY(jurisdiction));
    // Legacy fallback for CO: pre-jurisdiction keys. WY had no prior
    // state to migrate. One-shot read; the next save writes to the
    // new key shape.
    if (!raw && jurisdiction === 'CO') {
      raw = window.localStorage.getItem(LEGACY_FILTERS_KEY);
    }
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    // Defensively validate — a stale schema (e.g. an unknown status
    // string from before a future enum tightening) shouldn't crash the
    // hydration; fall back to defaults instead.
    const statuses: LeadListStatus[] = Array.isArray(parsed.statuses)
      ? parsed.statuses.filter((s: unknown): s is LeadListStatus =>
          typeof s === 'string' && (ALL_STATUSES as readonly string[]).includes(s))
      : [];
    if (statuses.length === 0) return null;
    return {
      statuses,
      hide_skipped:   !!parsed.hide_skipped,
      needs_location: !!parsed.needs_location,
      skip_ready:     !!parsed.skip_ready,
      skip_traced:    !!parsed.skip_traced,
    };
  } catch {
    return null;
  }
}

function saveStoredFilters(jurisdiction: 'CO' | 'WY', f: StoredFilters) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(FILTERS_STORAGE_KEY_BY(jurisdiction), JSON.stringify(f)); } catch {
    /* quota exceeded / private mode — non-fatal */
  }
}

function loadCollapsedGroups(jurisdiction: 'CO' | 'WY'): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    let raw = window.localStorage.getItem(COLLAPSED_STORAGE_KEY_BY(jurisdiction));
    if (!raw && jurisdiction === 'CO') {
      raw = window.localStorage.getItem(LEGACY_COLLAPSED_KEY);
    }
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((s: unknown) => typeof s === 'string'));
  } catch {
    return new Set();
  }
}

function saveCollapsedGroups(jurisdiction: 'CO' | 'WY', s: Set<string>) {
  if (typeof window === 'undefined') return;
  try { window.localStorage.setItem(COLLAPSED_STORAGE_KEY_BY(jurisdiction), JSON.stringify([...s])); } catch {
    /* quota exceeded / private mode — non-fatal */
  }
}

// Thin wrappers — both routes call LeadListInner with the appropriate
// jurisdiction. Existing /mi/leads continues to render Weld; /mi/wy/leads
// renders the Wyoming variant. All shared internals (filters, autosave
// inputs, skip-trace flow, view persistence) come for free.
export function LeadList()   { return <LeadListInner jurisdiction="CO" />; }
export function WyLeadList() { return <LeadListInner jurisdiction="WY" />; }

interface LeadListInnerProps {
  jurisdiction: 'CO' | 'WY';
}

function LeadListInner({ jurisdiction }: LeadListInnerProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const statuses      = parseStatusesFromUrl(searchParams);
  const hideSkipped   = searchParams.get('hide_skipped')   === 'true';
  const needsLocation = searchParams.get('needs_location') === 'true';
  const skipReady     = searchParams.get('skip_ready')     === 'true';
  const skipTraced    = searchParams.get('skip_traced')    === 'true';

  // Hydrate from localStorage on a BARE visit (no URL params). If
  // any URL param is present, the URL wins (shared-link semantics).
  // Per-jurisdiction key means the Weld and Wyoming lists remember
  // their own view-state independently. Runs once on mount.
  useEffect(() => {
    if ([...searchParams.keys()].length > 0) return;
    const stored = loadStoredFilters(jurisdiction);
    if (!stored) return;
    const next = new URLSearchParams();
    const sameAsDefault = stored.statuses.length === DEFAULT_STATUSES.length
      && stored.statuses.every(s => DEFAULT_STATUSES.includes(s));
    if (!sameAsDefault) next.set('status', stored.statuses.join(','));
    if (stored.hide_skipped)   next.set('hide_skipped',   'true');
    if (stored.needs_location) next.set('needs_location', 'true');
    if (stored.skip_ready)     next.set('skip_ready',     'true');
    if (stored.skip_traced)    next.set('skip_traced',    'true');
    if ([...next.keys()].length > 0) {
      // replace:true so the bare URL doesn't litter the back-stack.
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist current filter set to localStorage whenever it changes.
  useEffect(() => {
    saveStoredFilters(jurisdiction, {
      statuses, hide_skipped: hideSkipped, needs_location: needsLocation,
      skip_ready: skipReady, skip_traced: skipTraced,
    });
  }, [jurisdiction, statuses.join(','), hideSkipped, needsLocation, skipReady, skipTraced]);

  const [data, setData] = useState<LeadListResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());  // item ids
  const [pushOpen, setPushOpen] = useState(false);
  const [csvUploadOpen, setCsvUploadOpen] = useState(false);
  // Collapsed-group state: hydrate from localStorage on first render
  // so the operator's last-session expand/collapse layout survives
  // page reloads. Set key is `${source_kind}:${source_id}`. Per-
  // jurisdiction key so WY and CO don't share collapsed state.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => loadCollapsedGroups(jurisdiction));
  const [busyItem, setBusyItem] = useState<string | null>(null);

  // Candidate review modal — opened by clicking a row's ⚠ chip.
  // The whole item is captured so the modal can read the stored
  // candidates jsonb without an extra fetch.
  const [reviewItem, setReviewItem] = useState<LeadListItem | null>(null);

  // Keyboard flow plumbing for the city/state inputs. Two pieces:
  //   1. cityRefs: party_id → <input> for city. Populated by each
  //      SkipTraceInputs via registerCityRef.
  //   2. flatPartyOrder: party_ids in render order (across all visible
  //      groups, skipping collapsed groups). Derived from data.
  // On Enter from a state input, we look up the current party_id's
  // index in flatPartyOrder, then focus cityRefs[nextPartyId].
  const cityRefs = useRef<Map<string, HTMLInputElement | null>>(new Map());
  const registerCityRef = (partyId: string, el: HTMLInputElement | null) => {
    if (el) cityRefs.current.set(partyId, el);
    else    cityRefs.current.delete(partyId);
  };
  // Update flat party order whenever data + collapsed state change.
  // Each item id is unique within the lead list, but the SAME party
  // can appear under multiple sources — we dedupe on party_id and use
  // the FIRST appearance for keyboard nav (avoids focus thrash when
  // tabbing across a multi-source party).
  const flatPartyOrder = useMemo<string[]>(() => {
    if (!data) return [];
    const out: string[] = [];
    const seen = new Set<string>();
    for (const g of data.groups) {
      const key = `${g.source_kind}:${g.source_id}`;
      if (collapsedGroups.has(key)) continue;
      for (const it of g.items) {
        if (seen.has(it.party_id)) continue;
        seen.add(it.party_id);
        out.push(it.party_id);
      }
    }
    return out;
  }, [data, collapsedGroups]);
  const focusNextCity = (currentPartyId: string): boolean => {
    const i = flatPartyOrder.indexOf(currentPartyId);
    if (i < 0 || i >= flatPartyOrder.length - 1) return false;
    const nextEl = cityRefs.current.get(flatPartyOrder[i + 1]);
    if (!nextEl) return false;
    nextEl.focus();
    nextEl.select();
    return true;
  };

  async function reload() {
    try {
      const r = await leadListApi.get(statuses, { hideSkipped, needsLocation, skipReady, skipTraced, jurisdiction });
      setData(r);
      setErr(null);
    } catch (e: any) {
      setErr(e?.message ?? 'Failed to load lead list');
    }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [statuses.join(','), hideSkipped, needsLocation, skipReady, skipTraced, jurisdiction]);

  function toggleNeedsLocation() {
    const params = new URLSearchParams(searchParams);
    if (needsLocation) params.delete('needs_location');
    else {
      params.set('needs_location', 'true');
      // Needs-location + skip-ready are mutually exclusive (one means
      // "missing data", other means "complete data"). Activating one
      // implicitly clears the other so the operator doesn't end up
      // staring at an empty list.
      params.delete('skip_ready');
    }
    setSearchParams(params, { replace: false });
    setSelected(new Set());
  }

  function toggleSkipReady() {
    const params = new URLSearchParams(searchParams);
    if (skipReady) params.delete('skip_ready');
    else {
      params.set('skip_ready', 'true');
      params.delete('needs_location');  // mutually exclusive (see above)
    }
    setSearchParams(params, { replace: false });
    setSelected(new Set());
  }

  function toggleSkipTraced() {
    const params = new URLSearchParams(searchParams);
    if (skipTraced) params.delete('skip_traced');
    else            params.set('skip_traced', 'true');
    setSearchParams(params, { replace: false });
    setSelected(new Set());
  }

  function toggleStatus(s: LeadListStatus) {
    const isActive = statuses.includes(s);
    const next = isActive
      ? statuses.filter(x => x !== s)
      : [...statuses, s];
    if (next.length === 0) return; // never empty
    const params = new URLSearchParams(searchParams);
    // Only emit the param when the set differs from the default — keeps
    // URLs clean when the operator is on the default view.
    const sameAsDefault = next.length === DEFAULT_STATUSES.length
      && next.every(x => DEFAULT_STATUSES.includes(x));
    if (sameAsDefault) params.delete('status');
    else               params.set('status', next.join(','));
    setSearchParams(params, { replace: false });
    setSelected(new Set());
  }

  function toggleHideSkipped() {
    const params = new URLSearchParams(searchParams);
    if (hideSkipped) params.delete('hide_skipped');
    else             params.set('hide_skipped', 'true');
    setSearchParams(params, { replace: false });
    setSelected(new Set());
  }

  // ── Scroll restoration ───────────────────────────────────────────
  // Find the scrollable container (the Shell's overflow-y-auto div)
  // by walking up from a ref on the page root. Save scrollTop on
  // scroll into sessionStorage; restore once data has rendered.
  const pageRef = useRef<HTMLDivElement | null>(null);
  const scrollContainerRef = useRef<HTMLElement | null>(null);
  function findScrollContainer(): HTMLElement | null {
    let el: HTMLElement | null = pageRef.current?.parentElement ?? null;
    while (el) {
      const style = getComputedStyle(el);
      if (style.overflowY === 'auto' || style.overflowY === 'scroll') return el;
      el = el.parentElement;
    }
    return null;
  }
  useEffect(() => {
    scrollContainerRef.current = findScrollContainer();
    const c = scrollContainerRef.current;
    if (!c) return;
    const onScroll = () => {
      sessionStorage.setItem(SCROLL_STORAGE_KEY_BY(jurisdiction), String(c.scrollTop));
    };
    c.addEventListener('scroll', onScroll, { passive: true });
    return () => c.removeEventListener('scroll', onScroll);
  }, []);
  // Restore once data lands. Runs once per data-arrival; if the operator
  // navigates back and the data re-fetches, this fires again.
  const scrollRestoredRef = useRef(false);
  useEffect(() => {
    if (!data || scrollRestoredRef.current) return;
    const c = scrollContainerRef.current ?? findScrollContainer();
    if (!c) return;
    const stored = sessionStorage.getItem(SCROLL_STORAGE_KEY_BY(jurisdiction));
    if (stored) {
      const y = parseInt(stored, 10);
      if (Number.isFinite(y) && y > 0) c.scrollTop = y;
    }
    scrollRestoredRef.current = true;
  }, [data]);

  function toggleGroup(key: string) {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      saveCollapsedGroups(jurisdiction, next);
      return next;
    });
  }

  function toggleItem(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleGroupSelectAll(items: LeadListItem[]) {
    const ids = items.map(i => i.id);
    const allSelected = ids.every(id => selected.has(id));
    setSelected(prev => {
      const next = new Set(prev);
      if (allSelected) ids.forEach(id => next.delete(id));
      else             ids.forEach(id => next.add(id));
      return next;
    });
  }

  async function setItemStatus(item: LeadListItem, status: 'pending' | 'researched' | 'dropped') {
    setBusyItem(item.id);
    try {
      await leadListApi.setStatus(item.id, status);
      setSelected(prev => { const n = new Set(prev); n.delete(item.id); return n; });
      await reload();
    } catch (e: any) {
      setErr(e?.message ?? 'status change failed');
    } finally {
      setBusyItem(null);
    }
  }
  async function bulkDrop() {
    if (selected.size === 0) return;
    if (!confirm(`Drop ${selected.size} ${selected.size === 1 ? 'item' : 'items'} from the lead list?`)) return;
    const ids = [...selected];
    for (const id of ids) {
      try { await leadListApi.setStatus(id, 'dropped'); }
      catch { /* surface in next reload via status pill not changing */ }
    }
    setSelected(new Set());
    await reload();
  }

  // Recovery action for the "Close says yes, we say pending" failure
  // mode. Operator confirms the selected items already exist in Close
  // and we flip them to pushed + stamp their parties. No Close API
  // call is made — operator-asserted.
  async function bulkMarkPushedManual() {
    if (selected.size === 0) return;
    const msg = `Mark ${selected.size} ${selected.size === 1 ? 'item' : 'items'} as PUSHED without calling Close?\n\nUse this only when the leads already exist in Close (e.g. a previous push 502'd after Close created them but our DB never recorded it). This will NOT create duplicates in Close.`;
    if (!confirm(msg)) return;
    try {
      const r = await leadListApi.markPushedManual([...selected]);
      setSelected(new Set());
      await reload();
      alert(`Flipped ${r.items_flipped} items · stamped ${r.parties_stamped} parties${r.parties_already_stamped > 0 ? ` (${r.parties_already_stamped} parties already had push timestamps)` : ''}`);
    } catch (e: any) {
      setErr(e?.message ?? 'manual mark failed');
    }
  }

  const total = data?.total_visible ?? 0;
  const totalSelected = selected.size;
  const selectedItemIds = useMemo(() => [...selected], [selected]);

  // Materialize the selected item rows across all groups so the
  // sticky bottom bar can act on them (Copy names / Drop / Push).
  // Each item is already flat in the page payload; we just walk
  // the groups and filter by membership.
  const selectedItems = useMemo<LeadListItem[]>(() => {
    if (!data) return [];
    const out: LeadListItem[] = [];
    const seen = new Set<string>();
    for (const g of data.groups) {
      for (const it of g.items) {
        if (selected.has(it.id) && !seen.has(it.id)) {
          seen.add(it.id);
          out.push(it);
        }
      }
    }
    return out;
  }, [data, selected]);

  // Skip-trace agent handoff (shared hook + panels), scoped per jurisdiction
  // so the CO and WY lists keep separate run panels.
  const skip = useSkipTrace(`leadlist:${jurisdiction}`);
  function onSkipTrace() {
    if (selectedItems.length === 0) return;
    // Dedupe on party_id (same party can be on the list from multiple
    // sources). All recorder data is Weld County → county defaults to Weld.
    const seen = new Set<string>();
    const owners = selectedItems
      .filter(it => (seen.has(it.party_id) ? false : (seen.add(it.party_id), true)))
      .map(it => ({
        name: it.display_name_natural ?? it.display_name,
        trs: [] as string[],
        doc_numbers: it.most_recent_doc_number ? [it.most_recent_doc_number] : [],
        county: jurisdiction === 'WY' ? null : 'Weld',
      }));
    skip.submit(`Lead list · ${owners.length} lead${owners.length === 1 ? '' : 's'}`, owners);
  }

  // Copy-to-clipboard: same TPS-friendly formatter the SelectionBar
  // uses on AOI and pool-order pages. Dedupe on party_id since the
  // same party can be on the lead list from multiple sources — copying
  // the name twice would just waste the operator's TPS lookups.
  const [copied, setCopied] = useState(false);
  const [copiedDocs, setCopiedDocs] = useState(false);

  // Pipe-delimited most-recent-doc-numbers for the selected leads.
  // One doc per unique party (dedup on party_id); skip parties whose
  // most_recent_doc_number is null (no recorder docs). Format matches
  // the per-row "N docs ↗" CopyButton on AOI rows — pasteable straight
  // into the Weld recorder's multi-doc lookup.
  function buildCopyDocText(items: LeadListItem[]): string {
    const seenPartyIds = new Set<string>();
    const docs: string[] = [];
    for (const it of items) {
      if (seenPartyIds.has(it.party_id)) continue;
      seenPartyIds.add(it.party_id);
      if (it.most_recent_doc_number) docs.push(it.most_recent_doc_number);
    }
    return docs.join('|');
  }
  const selectedDocsCount = useMemo(() => {
    const seenPartyIds = new Set<string>();
    let n = 0;
    for (const it of selectedItems) {
      if (seenPartyIds.has(it.party_id)) continue;
      seenPartyIds.add(it.party_id);
      if (it.most_recent_doc_number) n++;
    }
    return n;
  }, [selectedItems]);

  function buildCopyText(items: LeadListItem[]): string {
    const seenPartyIds = new Set<string>();
    const rows = items
      .filter(it => {
        if (seenPartyIds.has(it.party_id)) return false;
        seenPartyIds.add(it.party_id);
        return true;
      })
      .map(it => ({
        party_id: it.party_id,
        // Natural form (First Last) is what TruePeopleSearch wants;
        // fall back to the recorder form when natural is unset.
        display_name: it.display_name_natural ?? it.display_name,
        has_phone: it.has_phone,
        has_address: it.has_address,
        source_label: it.display_name_natural ?? it.display_name,
      }));
    return formatNamesForTruePeopleSearch(rows);
  }

  async function writeClipboard(text: string) {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text; document.body.appendChild(ta); ta.select();
      document.execCommand('copy'); document.body.removeChild(ta);
    }
  }

  async function copyNames() {
    await writeClipboard(buildCopyText(selectedItems));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  async function copyDocs() {
    const text = buildCopyDocText(selectedItems);
    if (!text) return;
    await writeClipboard(text);
    setCopiedDocs(true);
    setTimeout(() => setCopiedDocs(false), 1800);
  }

  if (err) {
    return (
      <div className="px-8 py-7 max-w-[1280px] mx-auto">
        <div className="text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3">{err}</div>
      </div>
    );
  }
  if (!data) {
    return <div className="px-8 py-7 text-[12px] text-text-faint font-mono">loading lead list…</div>;
  }

  return (
    <div ref={pageRef} className="px-8 py-7 max-w-[1280px] mx-auto pb-20">
      <header className="flex items-end justify-between pb-6 border-b border-border gap-6">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">workspace · staging</div>
          <h1 className="font-display text-[28px] leading-tight">Lead <em className="text-accent not-italic">list</em></h1>
          <div className="text-[12px] text-text-dim mt-1.5 font-mono space-x-2">
            <span>{total} {total === 1 ? 'item' : 'items'} visible</span>
            <span className="text-text-ghost">·</span>
            <span>{totalSelected} selected</span>
            <span className="text-text-ghost">·</span>
            <span className={data.skip_trace.ready === data.skip_trace.total && data.skip_trace.total > 0 ? 'text-green' : 'text-text-dim'}>
              {data.skip_trace.ready} of {data.skip_trace.total} skip-trace ready
            </span>
          </div>
        </div>
        {/* CSV trace is selection-independent (operator uploads a file), so
            it lives in the header rather than the selection bar. */}
        <button onClick={() => setCsvUploadOpen(true)}
          title="Trace from a Cowork recorder CSV (full mailing addresses → Enformion Contact Enrich)."
          className="shrink-0 px-3 py-1.5 text-[12px] text-accent border border-accent/40 rounded hover:bg-accent-soft">
          ⌗ Trace from CSV
        </button>
      </header>

      {/* Skip-trace run panels (shared with AOI / party record). */}
      <SkipTracePanels skip={skip} />

      <div className="mt-4 flex items-center gap-2 flex-wrap">
        {(['pending','researched','pushed','dropped'] as LeadListStatus[]).map(s => {
          const active = statuses.includes(s);
          const count = data.counts[s] ?? 0;
          return (
            <button key={s} onClick={() => toggleStatus(s)}
              className={
                'px-3 py-1.5 text-[11px] font-mono rounded border ' +
                (active
                  ? STATUS_PILL_CLASS[s]
                  : 'border-border text-text-faint hover:text-text hover:border-border-bright')
              }>
              {STATUS_LABELS[s]} <span className="ml-1.5 opacity-70">{count}</span>
            </button>
          );
        })}
        {/* Hide-skipped filter — orthogonal to the status pills. When */}
        {/* active, parties with skipped_at IS NOT NULL are filtered  */}
        {/* out (server-side; counts reflect the visible set).        */}
        <button onClick={toggleHideSkipped}
          title={hideSkipped
            ? `Currently hiding skipped parties. Click to show ${data.skipped_party_count} skipped.`
            : `Hide ${data.skipped_party_count} parties marked "not worth pursuing"`}
          className={
            'ml-1 px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (hideSkipped
              ? 'border-red/40 bg-red/10 text-red'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          {hideSkipped ? '× Hiding skipped' : '× Hide skipped'}
          <span className="ml-1.5 opacity-70">{data.skipped_party_count}</span>
        </button>
        {/* Needs-location filter — orthogonal to status + hide_skipped. */}
        {/* Hides rows where both city and state are populated so the   */}
        {/* operator can focus on rows still needing skip-trace data.   */}
        <button onClick={toggleNeedsLocation}
          title={needsLocation
            ? `Showing ${data.skip_trace.needs_location} rows missing city or state. Click to show all.`
            : `Filter to ${data.skip_trace.needs_location} rows still missing city or state`}
          className={
            'px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (needsLocation
              ? 'border-amber/40 bg-amber/10 text-amber'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          ◐ Needs location
          <span className="ml-1.5 opacity-70">{data.skip_trace.needs_location}</span>
        </button>
        {/* Skip-ready filter — inverse of needs-location. Surfaces    */}
        {/* the rows ready for an Enformion trace. Mutually exclusive  */}
        {/* with needs-location at the toggle level (both can't help). */}
        <button onClick={toggleSkipReady}
          title={skipReady
            ? `Showing ${data.skip_trace.ready} rows with city + state captured. Click to show all.`
            : `Filter to ${data.skip_trace.ready} rows with city + state captured (ready for skip-trace)`}
          className={
            'px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (skipReady
              ? 'border-green/40 bg-green/10 text-green'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          ● Skip ready
          <span className="ml-1.5 opacity-70">{data.skip_trace.ready}</span>
        </button>
        {/* Skip-traced filter — Enformion already ran (matched or    */}
        {/* no-match). Useful for "show what I've already covered" or */}
        {/* "show what needs to be traced next" (toggle inverted via  */}
        {/* the status filter combo).                                  */}
        <button onClick={toggleSkipTraced}
          title={skipTraced
            ? `Showing ${data.skip_trace.traced} rows where Enformion already ran. Click to show all.`
            : `Filter to ${data.skip_trace.traced} rows where Enformion already ran`}
          className={
            'px-3 py-1.5 text-[11px] font-mono rounded border ' +
            (skipTraced
              ? 'border-blue/40 bg-blue/10 text-blue'
              : 'border-border text-text-faint hover:text-text hover:border-border-bright')
          }>
          ⌖ Skip traced
          <span className="ml-1.5 opacity-70">{data.skip_trace.traced}</span>
        </button>
      </div>

      {data.groups.length === 0 && (
        <div className="mt-12 bg-bg-card-soft border border-border rounded px-6 py-12 text-center">
          <div className="text-[13px] text-text-dim">No items match the current filter.</div>
          <div className="text-[11px] text-text-faint font-mono mt-2">
            Add parties to the lead list from any AOI or pool order page using the "+ Lead list" button on the selection bar.
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {data.groups.map(g => {
          const key = `${g.source_kind}:${g.source_id}`;
          const collapsed = collapsedGroups.has(key);
          const ids = g.items.map(i => i.id);
          const allSel = ids.length > 0 && ids.every(id => selected.has(id));
          return (
            <div key={key} className="bg-bg-card-soft border border-border rounded">
              <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-bg-input/30">
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleGroup(key)}
                    className="text-text-faint hover:text-text text-[12px] font-mono w-4">{collapsed ? '▸' : '▾'}</button>
                  <input type="checkbox" checked={allSel}
                    onChange={() => toggleGroupSelectAll(g.items)}
                    className="accent-accent" />
                  <span className="text-[10px] font-mono uppercase tracking-wider text-text-faint">
                    {g.source_kind === 'aoi' ? 'AOI' : 'Pool order'}
                  </span>
                  <Link to={g.source_kind === 'aoi' ? `/mi/aoi/${g.source_id}` : `/mi/pool-orders/${g.source_id}`}
                    className="text-[13px] text-text hover:text-accent">{g.source_label}</Link>
                  <span className="text-[11px] text-text-faint font-mono">· {g.items.length} {g.items.length === 1 ? 'item' : 'items'}</span>
                </div>
              </div>

              {!collapsed && (
                <div>
                  {g.items.map(item => (
                    <div key={item.id}
                      // Grid columns:
                      //  26px  ✓ checkbox
                      //  1.6fr name cell (with chips on second line)
                      //  160px city input
                      //  54px  state input
                      //  18px  skip-trace ready dot
                      //  auto  docs disclosure (chevron + count)
                      //  96px  status pill
                      //  auto  actions cluster
                      // Display: grid keeps the docs disclosure expansion
                      // grid-aligned when it spans all columns.
                      className="grid grid-cols-[26px_1.6fr_160px_54px_18px_auto_96px_auto] items-center gap-3 px-4 py-2 border-b border-border last:border-b-0 hover:bg-bg-hover/30">
                      <div>
                        <input type="checkbox" checked={selected.has(item.id)}
                          onChange={() => toggleItem(item.id)}
                          tabIndex={-1}
                          className="accent-accent" />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 min-w-0">
                          <Link to={`/mi/party/${item.party_id}`}
                            tabIndex={-1}
                            className={'text-[13px] hover:text-accent truncate ' + (item.skipped_at ? 'text-text-faint line-through' : 'text-text')}>
                            {item.display_name_natural ?? item.display_name}
                          </Link>
                          <PushedIndicator pushedAt={item.last_pushed_to_close_at} />
                          <SkipMarker      skippedAt={item.skipped_at} />
                          <SkipTraceOutcomeIndicators
                            deceased={item.deceased}
                            needsReview={item.needs_review}
                            skipTracedAt={item.skip_traced_at}
                            onReviewClick={item.enformion_candidates && item.enformion_candidates.length > 0
                              ? () => setReviewItem(item)
                              : undefined}
                          />
                          <HasContactIndicator hasPhone={item.has_phone} hasAddress={item.has_address} className="shrink-0" />
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <span className="text-[10px] text-text-faint font-mono truncate">{item.normalized_name}</span>
                          <span className={'shrink-0 px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider rounded ' + entityChipClass(item.entity_type)}>
                            {item.entity_type}
                          </span>
                        </div>
                      </div>
                      {/* City + State inputs — the active data-entry zone. */}
                      <SkipTraceInputs
                        partyId={item.party_id}
                        initialCity={item.skip_trace_city}
                        initialState={item.skip_trace_state}
                        registerCityRef={registerCityRef}
                        focusNext={() => focusNextCity(item.party_id)}
                        onSaved={() => { reload(); }}
                      />
                      <div className="text-center">
                        <SkipTraceReadyDot city={item.skip_trace_city} state={item.skip_trace_state} />
                      </div>
                      <div className="flex items-center justify-start">
                        <LeadListDocCopy docNumber={item.most_recent_doc_number} />
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={'px-1.5 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded border ' + STATUS_PILL_CLASS[item.status]}>
                          {STATUS_LABELS[item.status]}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 justify-end">
                        <AddPhoneInline
                          partyId={item.party_id}
                          hasPhone={item.has_phone}
                          onSaved={reload}
                        />
                        {item.status === 'pending' && (
                          <button onClick={() => setItemStatus(item, 'researched')} disabled={busyItem === item.id}
                            tabIndex={-1}
                            title="Mark as researched (you've filled in phone / address / notes)"
                            className="px-2 py-0.5 text-[10px] border border-blue/40 text-blue rounded hover:bg-blue/10 disabled:opacity-50">
                            ✓ researched
                          </button>
                        )}
                        {item.status === 'researched' && (
                          <button onClick={() => setItemStatus(item, 'pending')} disabled={busyItem === item.id}
                            tabIndex={-1}
                            title="Reset to pending"
                            className="px-2 py-0.5 text-[10px] border border-border text-text-faint rounded hover:text-text disabled:opacity-50">
                            ↩
                          </button>
                        )}
                        {(item.status === 'pending' || item.status === 'researched') && (
                          <button onClick={() => setItemStatus(item, 'dropped')} disabled={busyItem === item.id}
                            tabIndex={-1}
                            title="Drop from the list"
                            className="text-text-faint hover:text-red text-[14px] leading-none disabled:opacity-40">×</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {totalSelected > 0 && (
        <div className="fixed bottom-0 left-[220px] right-0 z-30 border-t border-accent/40 bg-bg-card-soft/95 backdrop-blur px-8 py-3 flex items-center gap-6">
          <div className="text-[12px] text-text-dim font-mono">
            <span className="text-text">{totalSelected}</span> selected
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setSelected(new Set())}
              className="px-3 py-1.5 text-[12px] text-text-faint border border-border rounded hover:text-text">
              Clear
            </button>
            <button onClick={copyNames}
              title="Copy selected names (one per line) for TruePeopleSearch lookup"
              className="px-3 py-1.5 text-[12px] text-text border border-border rounded hover:bg-bg-hover">
              {copied ? `Copied ${totalSelected} ✓` : `Copy ${totalSelected} names ↗`}
            </button>
            <button onClick={copyDocs}
              disabled={selectedDocsCount === 0}
              title={
                selectedDocsCount === 0
                  ? 'None of the selected leads have a recorder doc on file'
                  : `Copy ${selectedDocsCount} most-recent doc numbers (pipe-delimited, for the Weld recorder)`
              }
              className="px-3 py-1.5 text-[12px] text-text border border-border rounded hover:bg-bg-hover disabled:opacity-40 disabled:cursor-not-allowed">
              {copiedDocs ? `Copied ${selectedDocsCount} docs ✓` : `Copy ${selectedDocsCount} docs ↗`}
            </button>
            <label className="flex items-center gap-1.5 text-[11px] text-text-dim cursor-pointer select-none"
              title="Load the skip-trace results into Close when the run finishes">
              <input type="checkbox" checked={skip.loadToClose}
                onChange={e => skip.setLoadToClose(e.target.checked)} className="accent-accent" />
              Load to Close
            </label>
            <button onClick={onSkipTrace} disabled={skip.skipBusy}
              title="Hand the selected leads to the recorder skip-trace agent"
              className="px-3 py-1.5 text-[12px] text-blue border border-blue/40 rounded hover:bg-blue/10 disabled:opacity-50 disabled:cursor-not-allowed">
              {skip.skipBusy ? 'Submitting…' : `→ Skip trace ${totalSelected}`}
            </button>
            <button onClick={bulkDrop}
              className="px-3 py-1.5 text-[12px] text-text-faint border border-border rounded hover:text-red hover:border-red/40">
              Drop {totalSelected}
            </button>
            <button onClick={bulkMarkPushedManual}
              title="Mark these items as already pushed to Close (operator asserts the leads already exist there — no Close API call made). Use for recovery after a failed push."
              className="px-3 py-1.5 text-[12px] text-green border border-green/40 rounded hover:bg-green/10">
              ✓ Mark {totalSelected} pushed (manual)
            </button>
            <button onClick={() => setPushOpen(true)}
              className="px-3 py-1.5 text-[12px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
              Push {totalSelected} to Close →
            </button>
          </div>
        </div>
      )}

      {pushOpen && (
        <PushConfirmModal
          source_kind="lead_list"
          lead_list_item_ids={selectedItemIds}
          count={totalSelected}
          onClose={() => setPushOpen(false)}
          onPushed={async () => {
            setSelected(new Set());
            // Status flips to 'pushed' server-side; reload so the
            // pushed items disappear from the default filter.
            await reload();
          }}
        />
      )}

      {reviewItem && (
        <SkipTraceCandidatesModal
          partyId={reviewItem.party_id}
          partyDisplayName={reviewItem.display_name_natural ?? reviewItem.display_name}
          candidates={reviewItem.enformion_candidates ?? []}
          currentTahoeId={reviewItem.enformion_tahoe_id}
          onClose={() => setReviewItem(null)}
          onPicked={async () => { await reload(); }}
        />
      )}

      {csvUploadOpen && (
        <SkipTraceCsvUploadModal
          jurisdiction={jurisdiction}
          onClose={() => setCsvUploadOpen(false)}
          onCommitted={async () => { await reload(); }}
        />
      )}
    </div>
  );
}
